// @owners { team: patients-team }
import { type DeliveryMethod } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method';
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Description,
  H1,
  InputCheckbox,
  LgPadding,
  LgSpacing,
  MdPadding,
  MdSpacing,
  SecondaryPage,
  XsSpacing,
  XxsPadding,
  getBrand,
} from '@alto/design-system';
import {
  ADD_TO_SHIPMENT_MUTATION_KEY,
  CheckoutSplitShipmentHeader,
  CourierTipCard,
  DeliveryAddressCard,
  OrderBundlingPaymentSummary,
  PaymentMethodCard,
  PaymentSummary,
  ProgynyBillingNotice,
  SchedulingCard,
  SharpsContainerCard,
  SignatureConfirmationCard,
  getOrderBundlingCheckoutOrigin,
  useCheckoutTrackMailRequest,
  useCheckoutValidationErrors,
  useCheckoutViewed,
  useOrderBundlingOptions,
  useProgynyWizard,
  useSaveOrderText,
  useSignatureConfirmation,
} from '@alto/features';
import { type CartItem } from '@alto/orders_api/types/v1/cart_item';
import React, { useState } from 'react';
import { updateOrder } from '~shared/actions/checkout';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { MAIL_REQUEST_CONFIRMED_ERROR_MESSAGE } from '~shared/features/checkout/constants';
import { getIsOrderBundling } from '~shared/features/checkout/selectors/getCheckoutFlow';
import { getOrder, getOrderAddressId } from '~shared/features/checkout/selectors/getOrder';
import { getOrdersByIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import { type CheckoutValidationErrors } from '~shared/features/checkout/types';
import { type AnalyticsProps as TipAnalyticsProps } from '~shared/features/courier-tips/AnalyticsProps';
import { PRICING_INFO } from '~shared/features/my-meds/constants';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useOrderPricing, useQueryShipmentFeesAvailable } from '~shared/queries';
import { useIsMutating } from '~shared/react-query';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { CheckoutDeliverySummary } from './CheckoutDeliverySummary';
import { useGetItSoonerFlow } from './useGetItSoonerFlow';

const getCheckoutHeaderBackgroundColor = (inProgynyWizard: boolean) => {
  if (inProgynyWizard) {
    return COLORS.BACKGROUND_COLORS.PINK_LIGHTEST;
  }

  const brand = getBrand();
  return brand === 'fuzehealth'
    ? COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST
    : COLORS.BACKGROUND_COLORS.TERTIARY_LIGHTEST;
};

type CheckoutProps = {
  readonly handleEditTipAmount: (tipAmount: number) => void;
  readonly handleSaveOrder: (hasValidationErrors: boolean, validationErrors: CheckoutValidationErrors) => void;
  readonly inProgynyWizard: boolean;
  readonly isEditingOrder: boolean;
  readonly isTippable: boolean;
  readonly items: CartItem[];
  readonly orderIndex: number;
  readonly shipmentID: number | null | undefined;
  readonly tipAmount: number;
  readonly tipAnalyticsProps: TipAnalyticsProps;
};

export const Checkout = ({
  handleEditTipAmount,
  handleSaveOrder,
  inProgynyWizard,
  isEditingOrder,
  isTippable,
  items,
  orderIndex,
  shipmentID,
  tipAmount,
  tipAnalyticsProps,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: CheckoutProps) => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const order = useSelectorShared(getOrder);
  const addressID = useSelectorShared(getOrderAddressId);
  const { deliveryMethods } = useCartDeliveryMethods();
  const deliveryMethod: DeliveryMethod | undefined = deliveryMethods[orderIndex];
  const isSplitShipment = deliveryMethods.length > 1;
  const isFirstShipment = orderIndex === 0;
  const isLastShipment = orderIndex === deliveryMethods.length - 1;
  const { hasValidationErrors, validationErrors } = useCheckoutValidationErrors();

  const { openGetItSoonerFlow } = useGetItSoonerFlow();
  const { isLoading: fetchShipmentFeesLoading } = useQueryShipmentFeesAvailable();
  const { isOrderPricingLoading, isPaymentMethodRequired, orderPricing } = useOrderPricing({
    shipmentID: shipmentID ? shipmentID : undefined,
  });
  const fetchDeliveryWindowsLoading = useSelectorShared((state) => state.ui.loading.fetchDeliveryWindowsLoading);
  const isSavingOrder = useSelectorShared((state) => state.ui.loading.saveOrderLoading);
  const isSaveOrderButtonDisabled =
    fetchDeliveryWindowsLoading || isOrderPricingLoading || fetchShipmentFeesLoading || isSavingOrder;
  const { hasInjectables, hasOnlyInjectableCompounds } = usePrescriptionsInCart();
  const { hasItemsBilledToProgyny } = useProgynyWizard();
  const orderBundlingOrigin = getOrderBundlingCheckoutOrigin({ isEditingOrder });
  const { earliestUpcomingShipment } = useOrderBundlingOptions({
    address_id: addressID,
    origin: orderBundlingOrigin,
  });
  const isOrderBundling = useSelectorShared(getIsOrderBundling);
  const isAddToShipmentPending = !!useIsMutating({ mutationKey: ADD_TO_SHIPMENT_MUTATION_KEY });
  const isLoading = isSavingOrder || isAddToShipmentPending;
  const hidePaymentMethod =
    !isPaymentMethodRequired && isOrderBundling && !earliestUpcomingShipment?.payment_method_needed;
  const saveOrderText = useSaveOrderText({ isLastShipment });
  const { homeToSignConfirmationRequired } = useSignatureConfirmation();
  // Mail Request Section
  const [mailRequestChecked, setMailRequestChecked] = useState<boolean>(order.mail_request_confirmed ?? false);
  const { mailRequestRequired, mailRequestRequiredMessage, showMailRequestConfirmedError } =
    useCheckoutTrackMailRequest();

  useCheckoutViewed({
    shipmentID,
    items,
    isEditingOrder,
    isSplitShipment: deliveryMethods.length > 1,
    mailRequestRequired,
    orderPricing,
    isOrderPricingLoading,
  });

  // address can't change once an order is placed; patients usually try to update it via Account > Addresses
  const isSelectAddressDisabled = isEditingOrder || (!isFirstShipment && isSplitShipment) || isSaveOrderButtonDisabled;
  // for split shipments, payment method is hidden when payment is not required on the first shipment
  // for the second shipment, disable the payment method when one has been chosen for the first shipment
  // tip amount and tip payment method can still be set for the second shipment when courier
  const hasFirstShipmentPaymentMethod = !!useSelectorShared((state) => getOrdersByIndex(state)[0]?.payment_method_id);
  const isPaymentMethodDisabled =
    !isFirstShipment && isSplitShipment && isPaymentMethodRequired && hasFirstShipmentPaymentMethod;

  return (
    <SecondaryPage
      headerBackgroundColor={getCheckoutHeaderBackgroundColor(inProgynyWizard)}
      HeaderContent={
        <MdPadding topPadding={SPACING.STATIC.XXXL}>
          <H1>{isEditingOrder ? 'Edit Order' : 'Checkout'}</H1>
          {isSplitShipment ? <CheckoutSplitShipmentHeader orderIndex={orderIndex} /> : null}
        </MdPadding>
      }
    >
      <DeliveryAddressCard disabled={isSelectAddressDisabled} />
      <MdSpacing />
      <SchedulingCard
        isTippable={isTippable}
        openGetItSoonerFlow={openGetItSoonerFlow}
      />
      <MdSpacing />

      {homeToSignConfirmationRequired ? (
        <>
          <SignatureConfirmationCard />
          <MdSpacing />
        </>
      ) : null}

      {hasInjectables && !hasOnlyInjectableCompounds && isLastShipment ? (
        <>
          <SharpsContainerCard />
          <MdSpacing />
        </>
      ) : null}

      {!isOrderBundling && isTippable ? (
        <>
          <CourierTipCard
            analyticsProps={tipAnalyticsProps}
            handleEditTipAmount={handleEditTipAmount}
            tipAmount={tipAmount}
          />
          <MdSpacing />
        </>
      ) : null}

      {hidePaymentMethod ? null : (
        <>
          <PaymentMethodCard
            deliveryMethodMode={deliveryMethod?.mode}
            disabled={isPaymentMethodDisabled}
          />
          <MdSpacing />
        </>
      )}

      <CheckoutDeliverySummary />
      <MdSpacing />

      <ProgynyBillingNotice hasItemsBilledToProgyny={hasItemsBilledToProgyny} />

      {isOrderBundling ? (
        <OrderBundlingPaymentSummary
          showPaymentBreakdown
          origin={orderBundlingOrigin}
        />
      ) : (
        <PaymentSummary
          showZeroTipLineItem={isTippable}
          origin="Checkout"
        />
      )}
      <MdSpacing />

      {mailRequestRequired && isLastShipment ? (
        <XxsPadding>
          <InputCheckbox
            key="mail-request-checkbox"
            label=""
            checkPlacement="left"
            name="Mail request required"
            fullBleed
            noHighlight
            descriptions={<Description>{mailRequestRequiredMessage}</Description>}
            value={mailRequestChecked}
            onPress={() => {
              const newMailRequestChecked = !mailRequestChecked;
              setMailRequestChecked(newMailRequestChecked);
              dispatch(updateOrder({ mail_request_confirmed: newMailRequestChecked }));
              trackEvent({
                event: EVENTS.CHECKOUT__MAIL_REQUEST_CONFIRMED,
                params: { is_checked: newMailRequestChecked },
              });
            }}
          />
          {showMailRequestConfirmedError ? (
            <>
              <XsSpacing />
              <Description color={COLORS.TEXT_COLORS.DANGER}>{MAIL_REQUEST_CONFIRMED_ERROR_MESSAGE}</Description>
            </>
          ) : null}
        </XxsPadding>
      ) : null}

      <Button
        disabled={isSaveOrderButtonDisabled}
        loading={isLoading}
        testID="checkout-submit"
        onPress={() => {
          handleSaveOrder(hasValidationErrors, validationErrors);
        }}
      >
        {saveOrderText}
      </Button>
      <XsSpacing />

      {orderPricing?.price_unconfirmed ? (
        <>
          <LgSpacing />
          <Card title={PRICING_INFO.when_charged}>
            <LgPadding topPadding={SPACING.STATIC.NONE}>
              <Body>{PRICING_INFO.wont_be_charged_until}</Body>
            </LgPadding>
          </Card>
        </>
      ) : null}
    </SecondaryPage>
  );
};
